import React, {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";
import { createPortal } from "react-dom";

// Context for toast management
const ToastContext = createContext({
  showToast: () => {},
  hideToast: () => {},
  success: () => {},
  error: () => {},
  warning: () => {},
  info: () => {},
});

// Toast variants
const VARIANTS = {
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
  DEFAULT: "default",
};

// Icons for different toast types
const ToastIcon = ({ variant }) => {
  switch (variant) {
    case VARIANTS.SUCCESS:
      return (
        <svg
          className="w-4 h-4 text-sg-live"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      );
    case VARIANTS.ERROR:
      return (
        <svg
          className="w-4 h-4 text-sg-danger"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      );
    case VARIANTS.WARNING:
      return (
        <svg
          className="w-4 h-4 text-sg-warn"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      );
    case VARIANTS.INFO:
      return (
        <svg
          className="w-4 h-4 text-sg-ink-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    default:
      return (
        <svg
          className="w-4 h-4 text-sg-ink-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
  }
};

// Individual toast component
const Toast = ({
  id,
  title,
  message,
  variant = VARIANTS.DEFAULT,
  duration = 5000,
  onClose,
}) => {
  const [isExiting, setIsExiting] = useState(false);

  // Handle toast removal
  const handleClose = useCallback(() => {
    setIsExiting(true);
    // Wait for exit animation to complete
    setTimeout(() => {
      onClose(id);
    }, 300);
  }, [id, onClose]);

  // Auto-dismiss after duration
  useEffect(() => {
    if (duration) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  return (
    <div
      className={`sg-toast toast-${variant} pointer-events-auto w-80 ${isExiting ? "opacity-0 transition-opacity duration-[180ms]" : "sg-rise"}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="toast-icon mt-px">
        <ToastIcon variant={variant} />
      </div>
      <div className="toast-content flex-1 min-w-0">
        {title && (
          <div className="toast-title text-[13px] font-medium text-sg-ink">
            {title}
          </div>
        )}
        {message && (
          <div className="toast-message text-[13px] text-sg-ink-2">
            {message}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handleClose}
        className="toast-close h-5 w-5 flex items-center justify-center rounded-md text-sg-ink-3 hover:text-sg-ink hover:bg-sg-raised transition-colors duration-[120ms] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--sg-focus)]"
        aria-label="Close toast"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
};

// Toast container component
export const ToastContainer = ({ position = "top-right", children }) => {
  return createPortal(
    <div
      className={`toast-container ${position} fixed z-[60] flex flex-col gap-2 p-4 pointer-events-none ${
        position.includes("top") ? "top-0" : "bottom-0"
      } ${position.includes("left") ? "left-0 items-start" : "right-0 items-end"}`}
    >
      {children}
    </div>,
    document.body,
  );
};

// Toast provider for the application
export const ToastProvider = ({ children, defaultPosition = "top-right" }) => {
  const [toasts, setToasts] = useState([]);
  const [position, setPosition] = useState(defaultPosition);

  // Generate unique toast ID
  const generateId = () =>
    `toast-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  // Show toast
  const showToast = useCallback(
    (props) => {
      const id = props.id || generateId();

      setToasts((prevToasts) => [
        ...prevToasts,
        {
          id,
          position: props.position || position,
          ...props,
        },
      ]);

      return id;
    },
    [position],
  );

  // Hide toast
  const hideToast = useCallback((id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  // Helper methods for different toast types
  const success = useCallback(
    (props) => {
      return showToast({ variant: VARIANTS.SUCCESS, ...props });
    },
    [showToast],
  );

  const error = useCallback(
    (props) => {
      return showToast({ variant: VARIANTS.ERROR, ...props });
    },
    [showToast],
  );

  const warning = useCallback(
    (props) => {
      return showToast({ variant: VARIANTS.WARNING, ...props });
    },
    [showToast],
  );

  const info = useCallback(
    (props) => {
      return showToast({ variant: VARIANTS.INFO, ...props });
    },
    [showToast],
  );

  // Get toasts for each position
  const getToastsByPosition = useCallback(
    (pos) => {
      return toasts.filter((toast) => toast.position === pos);
    },
    [toasts],
  );

  // Context value
  const contextValue = {
    showToast,
    hideToast,
    success,
    error,
    warning,
    info,
    setPosition,
  };

  // Group toasts by position
  const positions = [...new Set(toasts.map((toast) => toast.position))];

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {positions.map((pos) => (
        <ToastContainer key={pos} position={pos}>
          {getToastsByPosition(pos).map((toast) => (
            <Toast key={toast.id} onClose={hideToast} {...toast} />
          ))}
        </ToastContainer>
      ))}
    </ToastContext.Provider>
  );
};

// Hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  return context;
};

export default { ToastProvider, useToast };
